<template>
  <div class="space-y-6 px-6 pb-6">
    <lf-heading3 class="">
      {{ $t("COMMON.NAME") }} & {{ $t("COMMON.DESCRIPTION") }}
    </lf-heading3>
    <lf-input
      :value="activeTemplate.name"
      :placeholder="$t('WORKFLOW.TEMPLATE_NAME')"
      :disabled="!canEditWorkflowTemplate"
      name="name"
      @blur="handleWorkflowSettingsChange('name', $event.target.value)"
    />
    <lf-dropdown
      v-if="isClientTemplate"
      name="branding_name"
      full-width
      show-clear-filter
      sort
      search-enabled
      :placeholder="$t('WORKFLOW.BRANDING')"
      :value="activeTemplate.branding_name || ''"
      :options="brandNameOptions"
      :is-loading="isBrandNamesLoading"
      :disabled="!canEditWorkflowTemplate"
      @change="handleBrandChange"
    />
    <lf-textarea
      :value="activeTemplate.description || ''"
      :placeholder="$t('WORKFLOW.TEMPLATE_DESCRIPTION')"
      :character-limit="TEXT_CHARACTER_LIMIT_DEFAULT"
      name="description"
      rows="4"
      show-label
      noresize
      :disabled="!canEditWorkflowTemplate"
      @blur="handleWorkflowSettingsChange('description', $event.target.value)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";

import { useQuery } from "@tanstack/vue-query";

import { AnalyticsEvent } from "@/enums/analytics";
import { getDropdownOptionsByKey } from "@/helpers/common";
import { TEXT_CHARACTER_LIMIT_DEFAULT } from "@/helpers/constants";
import { FIVE_MINUTES, TEN_MINUTES } from "@/helpers/constants/time";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import clientsApiService from "@/services/modules/clients";
import { useAnalyticsStore } from "@/stores/analytics";

import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfTextarea from "@/components/ui/inputs/LfTextarea.vue";
import LfHeading3 from "@/components/ui/text/LfHeading3.vue";

const {
  activeTemplate,
  canEditWorkflowTemplate,
  handleWorkflowSettingsChange,
  isClientTemplate,
} = useActiveWorkflowTemplate();

const { trackEvent } = useAnalyticsStore();

const { data: brandNamesData, isLoading: isBrandNamesLoading } = useQuery({
  queryKey: ["brand-names", "client", () => activeTemplate.value.client_id],
  queryFn: () =>
    clientsApiService.getBrandNames(activeTemplate.value.client_id!),
  enabled: isClientTemplate,
  staleTime: FIVE_MINUTES,
  gcTime: TEN_MINUTES,
});

const brandNameOptions = computed(() =>
  getDropdownOptionsByKey(brandNamesData.value?.data ?? [], "name")
);

const handleBrandChange = (brandName: string | number) => {
  const previousBrandSet = !!activeTemplate.value.branding_name;

  handleWorkflowSettingsChange("branding_name", brandName.toString());

  const properties = {
    client_id: activeTemplate.value.client_id,
    workflow_template_id: activeTemplate.value.id,
  };

  if (previousBrandSet) {
    trackEvent(AnalyticsEvent.WorkflowBrandChanged, {
      ...properties,
      previous_brand_set: true,
    });
  } else {
    trackEvent(AnalyticsEvent.WorkflowBrandSelected, {
      ...properties,
      has_brand: !!brandName,
    });
  }
};
</script>
